import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Auth,
  browserPopupRedirectResolver,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { FirebaseStorage, getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.GATSBY_FIREBASE_API_KEY,
  authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
  storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.GATSBY_FIREBASE_APP_ID,
  measurementId: process.env.GATSBY_FIREBASE_MEASUREMENT_ID,
};

const isConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId,
);

let cachedApp: FirebaseApp | null | undefined;
let cachedAuth: Auth | null | undefined;
let cachedFirestore: Firestore | null | undefined;
let cachedStorage: FirebaseStorage | null | undefined;

function getFirebaseApp(): FirebaseApp | null {
  if (typeof window === 'undefined' || !isConfigured) return null;
  if (cachedApp !== undefined) return cachedApp;
  cachedApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  return cachedApp;
}

export function getFirebaseAuth(): Auth | null {
  if (cachedAuth !== undefined) return cachedAuth;
  const app = getFirebaseApp();
  if (!app) {
    cachedAuth = null;
    return null;
  }

  try {
    cachedAuth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    cachedAuth = getAuth(app);
  }

  return cachedAuth;
}

export function getFirebaseFirestore(): Firestore | null {
  if (cachedFirestore !== undefined) return cachedFirestore;
  const app = getFirebaseApp();
  cachedFirestore = app ? getFirestore(app) : null;
  return cachedFirestore;
}

export function getFirebaseStorage(): FirebaseStorage | null {
  if (cachedStorage !== undefined) return cachedStorage;
  const app = getFirebaseApp();
  cachedStorage = app ? getStorage(app) : null;
  return cachedStorage;
}

export function assertFirebaseAuth(): Auth {
  const a = getFirebaseAuth();
  if (!a) {
    throw new Error(
      'Firebase Auth is not configured. Set GATSBY_FIREBASE_* in .env and restart the dev server.',
    );
  }
  return a;
}

export function assertFirestore(): Firestore {
  const db = getFirebaseFirestore();
  if (!db) {
    throw new Error(
      'Firestore is not configured. Set GATSBY_FIREBASE_* in .env and restart the dev server.',
    );
  }
  return db;
}

export function assertFirebaseStorage(): FirebaseStorage {
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error(
      'Firebase Storage is not configured. Set GATSBY_FIREBASE_* in .env and restart the dev server.',
    );
  }
  return storage;
}
